import {
    createAsyncThunk,
    createSelector,
    createSlice,
    PayloadAction
} from '@reduxjs/toolkit'
import { api, toRecord } from 'utils'
import {
    CreateCrewRequest,
    ListVirtualAttendeesResponse
} from 'types/swagger'
import { RequestStatus } from 'types'
import { RootState } from '../../../store'
import { Crew } from './types'
import { showNotification } from '../../notifications'

export type Slugs = { eventSlug: string, boothSlug: string }

export const inviteCrew = createAsyncThunk(
    'inviteCrew',
    async (args: { email: string } & Slugs, thunkAPI) => {
        const { eventSlug, boothSlug, email } = args
        const url = `/events/${eventSlug}/organizer/booths/${boothSlug}/crew/`
        const data: CreateCrewRequest = { email }
        const { error } = await api.safePost(url, data)
        if (error) {
            thunkAPI.dispatch(crewSlice.actions.setInviteError(`${error}`))
        } else {
            thunkAPI.dispatch(showNotification({
                body: 'User added',
                type: 'success',
            }))
            thunkAPI.dispatch(fetchCrew(args))
            thunkAPI.dispatch(crewSlice.actions.setInviteError(''))
        }
    },
)

export const deleteCrew = createAsyncThunk(
    'deleteCrew',
    async (args: { crew: Crew } & Slugs, thunkAPI) => {
        const { eventSlug, boothSlug } = args
        const userId = args.crew.id
        const url = `events/${eventSlug}/organizer/booths/${boothSlug}/crew/${userId}/`
        await api.delete(url)
        thunkAPI.dispatch(fetchCrew(args))
    },
)

export const fetchCrew = createAsyncThunk(
    'fetchCrew',
    async ({ eventSlug, boothSlug }: Slugs) => {
        const url = `events/${eventSlug}/organizer/booths/${boothSlug}/crew/`
        const response: ListVirtualAttendeesResponse = await api.get(url)
        return response.results
    },
)

const crewSlice = createSlice({
    name: 'crewSlice',
    initialState: {
        data: {},
        newCrewEmail: '',
        inviteError: '',
        inviteRequestStatus: 'idle' as RequestStatus,
    },
    reducers: {
        setInviteError(state, { payload }: PayloadAction<string>) {
            state.inviteError = payload
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCrew.fulfilled, (state, { payload }) => {
            state.data = toRecord(payload)
        })
        builder.addCase(inviteCrew.fulfilled, (state) => {
            state.inviteRequestStatus = 'succeeded'
        })
        builder.addCase(inviteCrew.pending, (state) => {
            state.inviteRequestStatus = 'loading'
        })
    }
})

export const crewSelector = createSelector(
    [(state: RootState) => state.booth.crew.data],
    (data) => Object.values(data),
)

export const selectNewCrewEmail = (state: RootState) => state.booth.crew.newCrewEmail
export const selectInviteError = (state: RootState) => state.booth.crew.inviteError
export const selectInviteRequestStatus = (state: RootState) => state.booth.crew.inviteRequestStatus
export const staffsSelector = (state: RootState) => Object.keys(state?.booth?.crew?.data || {})

export default crewSlice.reducer
